/**
 * Revital brief 29: when a sign-in provider fails she must be told WHICH one
 * failed and that email is still open to her. On the web two failures never
 * reached the screen:
 *  - Apple / Facebook redirect away and come back to /app/loading with
 *    ?error=... (or #error=...) and no session, so the spinner just waited.
 *  - Google's own button (GIS) reports its failure inside auth.service.
 * The screen that started the attempt stashes where she was; whoever sees the
 * failure leaves the provider name here; the auth screen reads it once.
 */
export type AuthProvider = 'google' | 'apple' | 'facebook';

const RETURN_KEY = 'authOAuthReturn';
const ERROR_KEY = 'authProviderError';

export function stashOAuthReturn(provider: AuthProvider): void {
  try {
    sessionStorage.setItem(
      RETURN_KEY,
      JSON.stringify({ provider, path: window.location.pathname + window.location.search }),
    );
  } catch {}
}

/** True when the provider sent her back with an error instead of a session. */
export function oauthErrorInUrl(): boolean {
  const q = new URLSearchParams(window.location.search);
  const h = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return q.has('error') || q.has('error_description') || h.has('error') || h.has('error_description');
}

/** On a failed OAuth return: remember which provider failed and return the
 *  page she started from (null when nothing was stashed). */
export function recordOAuthFailure(): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (!raw) return null;
    const { provider, path } = JSON.parse(raw) as { provider: AuthProvider; path: string };
    sessionStorage.setItem(ERROR_KEY, provider);
    return typeof path === 'string' && path.startsWith('/') ? path : null;
  } catch {
    return null;
  }
}

/** Read (once) the provider whose sign-in just failed, if any. */
export function takeProviderError(): AuthProvider | null {
  try {
    const p = sessionStorage.getItem(ERROR_KEY) as AuthProvider | null;
    sessionStorage.removeItem(ERROR_KEY);
    return p === 'google' || p === 'apple' || p === 'facebook' ? p : null;
  } catch {
    return null;
  }
}

/**
 * Captured when the bundle first evaluates, before the auth client gets a
 * chance to read (and possibly tidy) the return URL. Consumed once.
 */
let oauthErrorAtBoot = (() => {
  try {
    return oauthErrorInUrl();
  } catch {
    return false;
  }
})();

export function consumeBootOAuthError(): boolean {
  const hit = oauthErrorAtBoot || oauthErrorInUrl();
  oauthErrorAtBoot = false;
  return hit;
}
